import { z } from 'zod';

/* ------------------------------------------------------------ Uhren-Katalog des Ankaufs (ankauf_katalog) */

/** Merkmale eines Katalogeintrags – Bezeichnungen wie ANK_KATALOG_FELDER */
export const ANKAUF_MERKMALE = {
  groesse: 'Gehäusegröße (mm)',
  glas: 'Glas',
  zifferblatt: 'Zifferblatt',
  gehaeuse: 'Gehäusematerial',
  armband: 'Armbandmaterial',
  werk: 'Uhrwerk',
  geschlecht: 'Geschlecht',
} as const;
export type AnkaufMerkmal = keyof typeof ANKAUF_MERKMALE;
export type AnkaufMerkmale = Partial<Record<AnkaufMerkmal, string>> & { merkmale?: string[] };

/** Spalten für den Import (ANK_KATALOG_FELDER) */
export const ANKAUF_KATALOG_FELDER = {
  marke: 'Marke',
  modell: 'Modell',
  referenz: 'Referenz',
  referenzDetail: 'Referenz (detailliert)',
  sku: 'SKU',
  bild: 'Bild',
  bilderWeitere: 'Weitere Bilder',
  ...ANKAUF_MERKMALE,
  merkmale: 'Merkmale',
  anzahl: 'Gewicht',
} as const;
export type AnkaufKatalogFeld = keyof typeof ANKAUF_KATALOG_FELDER;

/** Kopfzeilen, die beim Import automatisch zugeordnet werden (ank_import_map) */
const KATALOG_ALIAS: Record<AnkaufKatalogFeld, string[]> = {
  marke: ['marke', 'brand', 'hersteller', 'manufacturer', 'vendor'],
  modell: ['modell', 'model', 'serie', 'collection', 'kollektion'],
  referenz: ['referenz', 'reference', 'ref', 'referenznummer', 'reference number', 'referencecode', 'reference code'],
  referenzDetail: ['referenz detail', 'detailedreferencecode', 'detailed reference', 'referenz detailliert', 'referenzdetail'],
  sku: ['sku', 'artikelnummer'],
  bild: ['bild', 'image', 'bild url', 'image url', 'imageurl', 'foto'],
  bilderWeitere: ['weitere bilder', 'images', 'bilder', 'additional images'],
  groesse: ['groesse', 'größe', 'casesize', 'case size', 'gehäusegröße', 'gehaeusegroesse', 'durchmesser'],
  glas: ['glas', 'crystal'],
  zifferblatt: ['zifferblatt', 'dialcolor', 'dial color', 'dial'],
  gehaeuse: ['gehaeuse', 'gehäuse', 'casematerial', 'case material', 'gehäusematerial'],
  armband: ['armband', 'braceletmaterial', 'bracelet material', 'armbandmaterial'],
  werk: ['werk', 'uhrwerk', 'movement', 'kaliber'],
  geschlecht: ['geschlecht', 'gender'],
  merkmale: ['merkmale', 'features', 'funktionen'],
  anzahl: ['gewicht', 'anzahl', 'popularity', 'beliebtheit'],
};
const kopfNorm = (k: string) => k.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');

export type AnkaufKatalogZuordnung = Partial<Record<AnkaufKatalogFeld, number>>;

/** Kopfzeile → Spaltennummer je Feld; jede Spalte höchstens einmal */
export function katalogZuordnung(kopf: string[]): AnkaufKatalogZuordnung {
  const z: AnkaufKatalogZuordnung = {};
  kopf.forEach((k, i) => {
    const n = kopfNorm(k);
    const feld = (Object.keys(KATALOG_ALIAS) as AnkaufKatalogFeld[]).find((f) => z[f] === undefined && KATALOG_ALIAS[f].some((a) => kopfNorm(a) === n));
    if (feld !== undefined) z[feld] = i;
  });
  return z;
}

/**
 * CSV lesen, wie Excel sie schreibt: Trennzeichen ; , oder Tab (aus der Kopfzeile erkannt), Anführungszeichen
 * mit "" als Escape, Zeilenumbrüche in Feldern, BOM. Leere Zeilen bleiben stehen, damit die Zeilennummern
 * denen in Excel entsprechen – nur der Umbruch am Dateiende ergibt keine Zeile.
 */
export function csvLesen(text: string, maxZeilen = 200_001): string[][] {
  const t = text.replace(/^\uFEFF/, '');
  const ende = t.search(/\r?\n/);
  const ersteZeile = ende < 0 ? t : t.slice(0, ende);
  const zaehle = (z: string) => ersteZeile.split(z).length;
  const trenner = [';', '\t', ','].reduce((best, z) => (zaehle(z) > zaehle(best) ? z : best), ';');
  const zeilen: string[][] = [];
  let zeile: string[] = [];
  let feld = '';
  let inQuote = false;
  const zeileFertig = () => {
    zeile.push(feld);
    zeilen.push(zeile);
    zeile = [];
    feld = '';
  };
  for (let i = 0; i < t.length && zeilen.length < maxZeilen; i++) {
    const c = t[i]!;
    if (inQuote) {
      if (c !== '"') feld += c;
      else if (t[i + 1] === '"') {
        feld += '"';
        i++;
      } else inQuote = false;
    } else if (c === '"' && feld === '') inQuote = true;
    else if (c === trenner) {
      zeile.push(feld);
      feld = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && t[i + 1] === '\n') i++;
      zeileFertig();
    } else feld += c;
  }
  if (zeilen.length < maxZeilen && (feld !== '' || zeile.length)) zeileFertig();
  return zeilen;
}

/** Suchtext normalisieren (ank_norm): klein, Umlaute ausgeschrieben, nur Buchstaben, Ziffern, . - / */
export function ankaufNorm(s: string): string {
  const ersatz: Record<string, string> = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss', é: 'e', è: 'e', ê: 'e', à: 'a', á: 'a', ç: 'c', '&': ' ' };
  return s
    .trim()
    .toLowerCase()
    .replace(/[äöüßéèêàáç&]/g, (z) => ersatz[z]!)
    .replace(/[^a-z0-9.\-/ ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Trennzeichen in Referenzen und Namen spielen bei der Suche keine Rolle („116500ln“ findet „116500-LN“) */
const ohneTrenner = (s: string) => s.replace(/[-./]/g, '');

/** Suchtext eines Eintrags: normalisiert und zusätzlich ohne Trennzeichen */
export function katalogSuchtext(e: { marke: string; modell: string; referenz: string; referenzDetail: string }): string {
  const t = ankaufNorm(`${e.marke} ${e.modell} ${e.referenz} ${e.referenzDetail}`);
  return `${t} ${ohneTrenner(t)}`.slice(0, 1000);
}

/** Suchwörter für den Katalog: normalisiert, ohne Trennzeichen, höchstens sechs (wie ank_suche) */
export function katalogSuchwoerter(q: string): string[] {
  return [...new Set(ankaufNorm(q).split(' ').map(ohneTrenner).filter(Boolean))].slice(0, 6);
}

const Text = (max: number) => z.string().trim().max(max, `Höchstens ${max} Zeichen`).default('');
const BildUrl = z.string().max(500, 'Bildadresse zu lang').regex(/^https?:\/\/[^\s"<>]+$/i, 'Bitte nur Bildadressen mit http(s):// angeben.');

/** Eintrag anlegen oder ändern (ank_katalog_speichern) */
export const AnkaufKatalogEintragSchema = z.object({
  marke: z.string().trim().min(1, 'Bitte die Marke angeben.').max(80, 'Höchstens 80 Zeichen'),
  modell: z.string().trim().min(1, 'Bitte das Modell angeben.').max(120, 'Höchstens 120 Zeichen'),
  referenz: Text(80),
  referenzDetail: Text(80),
  sku: Text(80),
  /** Liste oder Text mit Leerzeichen/Zeilenumbrüchen getrennt */
  bilder: z
    .preprocess((v) => (typeof v === 'string' ? v.split(/[\s|;,]+/) : v ?? []), z.array(z.string().trim()))
    .transform((l) => [...new Set(l.filter(Boolean))])
    .pipe(z.array(BildUrl).max(8, 'Höchstens 8 Bilder')),
  merkmale: z
    .object({
      groesse: Text(80),
      glas: Text(80),
      zifferblatt: Text(80),
      gehaeuse: Text(80),
      armband: Text(80),
      werk: Text(80),
      geschlecht: Text(80),
      /** Liste oder Text mit ; | , getrennt */
      merkmale: z
        .preprocess((v) => (typeof v === 'string' ? v.split(/\s*[;|,]\s*/) : v ?? []), z.array(z.string().trim().max(60, 'Höchstens 60 Zeichen je Merkmal')))
        .transform((l) => l.filter(Boolean))
        .pipe(z.array(z.string()).max(30, 'Höchstens 30 Merkmale')),
    })
    .default({}),
  anzahl: z.coerce.number({ invalid_type_error: 'Bitte eine Zahl angeben.' }).int('Bitte eine ganze Zahl angeben.').min(0).max(1_000_000).default(0),
  aktiv: z.boolean().default(true),
});
export type AnkaufKatalogEingabe = z.input<typeof AnkaufKatalogEintragSchema>;
export type AnkaufKatalogDaten = z.output<typeof AnkaufKatalogEintragSchema>;

/** Leere Merkmale weglassen – so wird gespeichert */
export function merkmaleKompakt(m: AnkaufKatalogDaten['merkmale']): AnkaufMerkmale {
  const r: AnkaufMerkmale = {};
  for (const k of Object.keys(ANKAUF_MERKMALE) as AnkaufMerkmal[]) if (m[k]) r[k] = m[k];
  if (m.merkmale.length) r.merkmale = m.merkmale;
  return r;
}

export interface AnkaufKatalogEintrag {
  id: string;
  marke: string;
  modell: string;
  referenz: string;
  referenzDetail: string;
  sku: string;
  bilder: string[];
  merkmale: AnkaufMerkmale;
  anzahl: number;
  aktiv: boolean;
  geaendertAm: string;
}

export const AnkaufKatalogAbfrage = z.object({
  q: z.string().trim().max(120).optional(),
  status: z.enum(['aktiv', 'inaktiv']).optional(),
  cursor: z.string().max(1000).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

export interface AnkaufKatalogSeite {
  eintraege: AnkaufKatalogEintrag[];
  weiter: string | null;
  /** Treffer der Suche bzw. des Filters */
  treffer: number;
  /** über den ganzen Katalog */
  gesamt: number;
  aktiv: number;
  marken: number;
}

export const ANKAUF_IMPORT_MODUS = { ergaenzen: 'Ergänzen und aktualisieren', ersetzen: 'Ersetzen – nicht enthaltene Einträge werden inaktiv' } as const;
export type AnkaufImportModus = keyof typeof ANKAUF_IMPORT_MODUS;
export const ANKAUF_KATALOG_MAX_BYTES = 25 * 1024 * 1024;
export const ANKAUF_KATALOG_MAX_ZEILEN = 200_000;

export const AnkaufKatalogImportSchema = z.object({
  csv: z.string().min(1, 'Die Datei ist leer.').max(ANKAUF_KATALOG_MAX_BYTES, 'Die Datei ist zu groß (höchstens 25 MB).'),
  /** Feld → Spaltennummer; fehlt sie, wird aus der Kopfzeile zugeordnet */
  zuordnung: z.record(z.string(), z.number().int().min(0).max(500)).optional(),
  modus: z.enum(['ergaenzen', 'ersetzen']).default('ergaenzen'),
});
export type AnkaufKatalogImportEingabe = z.input<typeof AnkaufKatalogImportSchema>;

export interface AnkaufKatalogImportErgebnis {
  importiert: number;
  /** davon neu angelegt */
  neu: number;
  uebersprungen: number;
  /** im Modus „ersetzen“ inaktiv gesetzt */
  deaktiviert: number;
  beispiele: string[];
  /** die ersten Fehler mit Zeilennummer der Datei */
  fehler: { zeile: number; meldung: string }[];
}

/* Öffentlich: Auswahl im Anfrageformular und in der Shop-Section */

export interface AnkaufKatalogGruppe {
  marke: string;
  /** leer bei Marken */
  modell: string;
  /** Zahl der Referenzen */
  anzahl: number;
  bild: string | null;
}

export interface AnkaufKatalogTreffer {
  marke: string;
  modell: string;
  referenz: string;
  referenzDetail: string;
  sku: string;
  bild: string | null;
  merkmale: AnkaufMerkmale;
}

export interface AnkaufKatalogTrefferSeite {
  eintraege: AnkaufKatalogTreffer[];
  weiter: string | null;
}

export const AnkaufKatalogOeffentlichAbfrage = z.object({
  q: z.string().trim().max(120).default(''),
  marke: z.string().trim().max(80).default(''),
  modell: z.string().trim().max(120).default(''),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(96).default(24),
});
